"use strict"

class VueSelectCompat {
    static adapterForData(items, page_size) {
        if (this.select2ArrayAdapter) {
            return class extends this.select2ArrayAdapter {
                current(cb) {
                    return cb(items.filter(i => i.selected))
                }
                query(params, callback) {
                    if (!params.page) {
                        params.page = 1;
                    }

                    const term = (params.term || "").toString().toLowerCase();

                    let visible_items = term
                        ? items.filter(item =>
                              (item.text || "")
                                  .toString()
                                  .toLowerCase()
                                  .includes(term)
                          )
                        : items;

                    callback({
                        results: visible_items.slice(
                            (params.page - 1) * page_size,
                            params.page * page_size
                        ),
                        pagination: {
                            more: params.page * page_size < visible_items.length,
                        },
                    })
                }
            }
        } else {
            return null
        }
    }

    /**
     * Select2 hides the native <select> and draws its own role="combobox"
     * span, which is named only by its rendered value. This gives that span
     * the select's accessible name and copies its ARIA state across, so a
     * `label(for=id)`, `aria-label`, `aria-labelledby`, `aria-describedby`,
     * `aria-invalid`, `aria-required` or `aria-busy` on the <select> reaches
     * assistive tech. Clicking the label focuses the combobox.
     *
     * Safe to call repeatedly: it only sets attributes and rebinds its own
     * namespaced label click handler. Select2 rebuilds its container on
     * every init, so call it after each one.
     *
     * @param {HTMLSelectElement} select
     * @param {?string} fallbackName Used only when nothing else names the control
     */
    static applyAria(select, fallbackName) {
        const instance = $(select).data("select2")
        if (!instance || !instance.$container) {
            return
        }
        const $selection = instance.$container.find(".select2-selection").first()
        if (!$selection.length) {
            return
        }
        const selection = $selection[0]
        if (!selection.id) {
            selection.id = `vue-select-a11y-${++VueSelectCompat.uid}`
        }

        const $labels = select.id ?
            $(document.querySelectorAll(`label[for="${CSS.escape(select.id)}"]`)) :
            $()
        $labels.each(function () {
            if (!this.id) {
                this.id = `${selection.id}-label`
            }
        })

        // Name sources, most specific first.
        let nameIds = select.getAttribute("aria-labelledby")
        let nameText = null
        if (!nameIds && $labels.length) {
            nameIds = $labels.map(function () { return this.id }).get().join(" ")
        }
        if (!nameIds) {
            nameText = select.getAttribute("aria-label") || fallbackName || null
        }

        // Single selects: Select2 labels the combobox with its rendered value.
        // Keep that id so the value is still read, after the name.
        const $rendered = $selection.find(".select2-selection__rendered").first()
        const renderedId = (!select.multiple && $rendered.attr("id")) || null
        if (nameText) {
            selection.setAttribute("aria-label", nameText)
            nameIds = selection.id // self-reference resolves to aria-label
        } else {
            selection.removeAttribute("aria-label")
        }
        const labelledBy = [nameIds, renderedId].filter(Boolean).join(" ")
        if (labelledBy) {
            selection.setAttribute("aria-labelledby", labelledBy)
        }

        // Multi-selects take focus in their inline search box, so it needs
        // the state as well as the combobox.
        const $stateTargets = $selection.add(
            select.multiple ? instance.$container.find(".select2-search__field") : $()
        )
        for (const attr of VueSelectCompat.mirroredAriaAttrs) {
            const value = select.getAttribute(attr)
            if (value === null || value === "false") {
                $stateTargets.removeAttr(attr)
            } else {
                $stateTargets.attr(attr, value)
            }
        }

        // The search boxes (inline for multi-selects, in the dropdown for
        // searchable singles) are unnamed searchboxes otherwise.
        const searchName = nameText ||
            (nameIds || "").split(" ")
                .map(id => document.getElementById(id))
                .filter(Boolean)
                .map(el => el.getAttribute("aria-label") || el.textContent.trim())
                .join(" ")
        if (searchName) {
            instance.$container.find(".select2-search__field").attr("aria-label", searchName)
            if (instance.dropdown && instance.dropdown.$search) {
                instance.dropdown.$search.attr("aria-label", searchName)
            }
        }

        $labels
            .off("click.vueSelectA11y")
            .on("click.vueSelectA11y", evt => {
                evt.preventDefault()
                const $inlineSearch = instance.$container.find(".select2-search__field")
                if (select.multiple && $inlineSearch.length) {
                    $inlineSearch.trigger("focus")
                } else {
                    $selection.trigger("focus")
                }
            })
    }

    /**
     * @param {HTMLSelectElement} select
     */
    static removeAria(select) {
        if (select.id) {
            $(document.querySelectorAll(`label[for="${CSS.escape(select.id)}"]`))
                .off("click.vueSelectA11y")
        }
    }
}
VueSelectCompat.select2ArrayAdapter = null
VueSelectCompat.uid = 0
VueSelectCompat.mirroredAriaAttrs = [
    "aria-describedby",
    "aria-invalid",
    "aria-required",
    "aria-busy",
]

// Plain definition, usable as-is by both Vue 2 (via the Vue.extend(...)
// constructor below, or `components: { vueSelect: VueSelectDef }`) and
// Vue 3 (via `app.component('vueSelect', VueSelectDef)` or
// `components: { vueSelect: VueSelectDef }`). Vue.extend() returns a Vue 2
// constructor, which Vue 3 cannot consume, so converted pages must use this
// object instead of the `VueSelect` constructor below.
const VueSelectDef = {
    // Declared explicitly: Vue 3 auto-forwards an undeclared listener as a
    // native DOM attribute on top of invoking it via $emit, double-firing a
    // parent's v-model:model unless the emit is declared here. See
    // loading-button.js / VUE3-MIGRATION.md for the same gotcha.
    emits: ['update:model'],
    template: '<select '+
        'class="direct-select2" ' +
        'v-bind:name="name" ' +
        'v-bind:multiple="multiple" ' +
        'v-bind:data-placeholder="label" ' +
        'v-bind:data-searchable="searchable" ' +
        'style="width: 100%" ' +
        '>' +
        '</select>',
    name: "vueSelect",
    props: {
        ajax: {
            type: Object,
            required: false,
        },
        closeOnSelect: {
            type: Boolean,
            default: true,
        },
        infiniteScroll: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
        },
        model: {
            type: [String, Number, Array],
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        name: {
            type: String
        },
        options: {
            type: Array,
            default() {
                return []
            },
        },
        searchable: {
            type: Boolean,
            required: false,
            default: true,
        },
        /**
         * The display text for the initial `model` value, used to seed the
         * selection when `ajax` is set and that value hasn't come back from
         * a query yet.
         */
        selectedText: {
            type: String,
            required: false,
        },
        templateResult: {
            type: Function,
            required: false,
        },
        templateSelection: {
            type: Function,
            required: false,
        },
        dropdownCssClass: {
            type: String,
            required: false,
            default: ""
        }
    },
    data() {
        return {
            saneOptions: null,
        }
    },
    computed: {
        /**
         * @returns {string}
         */
        ariaAttrsKey() {
            return ["id", "aria-label", "aria-labelledby", ...VueSelectCompat.mirroredAriaAttrs]
                .map(attr => this.$attrs[attr])
                .join("|")
        },
    },
    mounted() {
        this.reinit()
        $(this.$el)
            .on("select2:open", () => this.applyAria())
            .on("change", function(evt) {
                if(this._events && !this._events["update:model"]) {
                    console.warn(
                        "Model update is not being tracked, please add .sync or an explicit event handler"
                    )
                }
                if(this.multiple) {
                    let selected = []
                    for(var i = 0; i < evt.currentTarget.selectedOptions.length; i++) {
                        selected.push(evt.currentTarget.selectedOptions[i].value)
                    }
                    this.$emit('update:model', selected)
                } else {
                    this.$emit('update:model', evt.currentTarget.value)
                }
            }.bind(this));
    },
    beforeDestroy() {
        VueSelectCompat.removeAria(this.$el);
        $(this.$el).select2("destroy");
    },
    methods: {
        reinit() {
            this.updateSaneOptions();
            let adapter = (this.infiniteScroll && !this.ajax) ?
                VueSelectCompat.adapterForData(this.saneOptions, 50) :
                null;

            const $modal = $(this.$el).closest('.modal');
            const $modalBody = $modal.find('.modal-body');
            const dropdownParent = $modalBody.length ? $modalBody : ($modal.length ? $modal : $(document.body));

            $(this.$el)
                .empty()
                .select2({
                    data: (adapter || this.ajax) ? null : this.saneOptions,
                    ajax: adapter ? {} : this.ajax,
                    closeOnSelect: this.closeOnSelect,
                    dataAdapter: adapter,
                    minimumResultsForSearch: this.searchable ? 0 : Infinity,
                    templateResult: this.templateResult,
                    templateSelection: this.templateSelection,
                    dropdownCssClass: this.dropdownCssClass,
                    dropdownParent: dropdownParent,
                    width: '100%'
                });

            $(this.$el).on('select2:open', () => {
                const instance = $(this.$el).data('select2');
                if (instance && instance.dropdown) {
                    setTimeout(() => {
                        if (typeof instance.dropdown._positionDropdown === 'function') {
                            instance.dropdown._positionDropdown();
                        }
                    }, 0);
                }
            });

            if (this.ajax && this.model && this.selectedText) {
                $(this.$el)
                    .append(new Option(this.selectedText, this.model, true, true))
                    .trigger("change");
            } else if(this.infiniteScroll) {
                $(this.$el).trigger("change.select2");
            } else {
                $(this.$el).val(this.model).trigger("change");
            }
            this.applyAria();
        },
        applyAria() {
            VueSelectCompat.applyAria(this.$el, this.label);
        },
        updateSaneOptions() {
            if(this.options.length == 0) {
                this.saneOptions = [];
            } else {
                if(
                    (typeof this.options[0] == "object") ||
                    (typeof this.options[0] == "function")
                ) {
                    if(this.options[0].hasOwnProperty("value")) {
                        console.warn(
                            "Options have been supplied in the Select2 1.x format (value, text). To work in future they will need to be updated to Select2 4.x format (id, text)"
                        )
                        this.saneOptions = this.options.map(o => ({
                            id: o.value,
                            text: o.text,
                            selected: this.multiple ?
                                this.model.includes(o.value) :
                                o.value == this.model,
                        }))
                    } else {
                        this.saneOptions = this.options.map(
                            o => Object.assign(
                                {},
                                o,
                                {
                                    selected: this.multiple ?
                                        this.model.includes(o.id) :
                                        o.id == this.model,
                                }
                            )
                        )
                    }
                } else {
                    this.saneOptions = this.options.map(o => ({
                        id: o,
                        text: o,
                        selected: this.multiple ?
                            this.model.includes(o) :
                            o == this.model,
                    }))
                }
            }
        }
    },
    watch: {
        // id / aria-* attributes fall through onto the <select>; re-mirror
        // them when the parent changes one (e.g. aria-invalid on an error).
        // Vue 2 replaces $attrs on every parent render, so only act when an
        // attribute we care about has actually changed.
        ariaAttrsKey() {
            this.$nextTick(() => this.applyAria());
        },
        selectedText(v, o) {
            if (v !== o) {
                this.reinit();
            }
        },
        model(v, o) {
            // NOTE: for multi-select, select2 sends the whole array as the
            // value, but Vue's change detection detects that as "whole thing
            // changed" each time, and setting the value ALSO triggers a model
            // update.
            if(this.infiniteScroll) {
                if(v !== o) {
                    if(this.multiple) {
                        this.saneOptions.forEach(
                            opt => opt.selected = this.model.includes(opt.id) ? true : null
                        )
                    } else {
                        this.saneOptions.forEach(
                            opt => opt.selected = opt.id == v ? true : null
                        )
                    }
                }
                $(this.$el).trigger("change.select2")
            } else if(
                !this.multiple || JSON.stringify(v) !== JSON.stringify(o)
            ) {
                $(this.$el)
                    .val(v)
                    .trigger("change")
            }
        },
        options(v, old) {
            if(
                v.length == old.length &&
                v.length < 32768 &&
                JSON.stringify(v) == JSON.stringify(old)
            ) {
                console.log("No change")
            } else {
                this.reinit()
            }
        }
    }
}

let VueSelect = Vue.extend(VueSelectDef)
$.fn.select2.amd.require(
    ["select2/data/array"],
    ArrayData => VueSelectCompat.select2ArrayAdapter = ArrayData
)
